import React from 'react';
import { Chip, ChipProps, SxProps, useTheme } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

// Type for MUI Chip variants matching the new theme system
export type TagVariant =
  | 'neutral'
  | 'neutral2'
  | 'brand'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'condition';

export interface TagProps
  extends Omit<ChipProps, 'children' | 'label' | 'variant' | 'size' | 'color'>, PounceStyleProps {
  /** Additional styles to apply (combined with pounce props) */
  sx?: ChipProps['sx'];

  /** Badge variant combining color and style. Defaults to `neutral`. Ignored when color prop is provided. */
  variant?: TagVariant;

  /** Whether the Tag is selected or not */
  selected?: boolean;

  /** The content to display inside the badge */
  children: React.ReactNode;
}

/**
 * Tag component built on top of MUI Chip.
 *
 * Tags are compact elements used to label, categorize, or highlight metadata within
 * an interface. They help communicate status, type, or attributes at a glance and
 * are often used in tables, filters, cards, or lists to increase scannability and context.
 *
 * Features:
 * - Color options (neutral, neutral2, brand, accent, success, warning, error, info)
 * - Selected state support
 * - Full pounce style props compatibility
 */
export const Tag = React.forwardRef<HTMLDivElement, TagProps>(function Tag(
  { variant = 'neutral', selected = false, children, sx, ...rest },
  ref,
) {
  const {
    palette: { componentTokens },
  } = useTheme();
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const combinedSx = {
    ...pounceStyles,
    ...(selected && {
      backgroundColor: componentTokens?.tags.colors[variant]?.background.selected,
      borderColor: componentTokens?.tags.colors[variant]?.stroke.selected,
      '&:hover': {
        backgroundColor: componentTokens?.tags.colors[variant]?.background.selected,
        borderColor: componentTokens?.tags.colors[variant]?.stroke.selected,
        cursor: 'inherit',
      },
    }),
    ...sx,
  } as SxProps;

  return (
    <Chip
      ref={ref}
      label={children}
      variant={variant}
      aria-atomic="true"
      sx={combinedSx}
      {...otherProps}
      size="tag-default"
    />
  );
});
